import * as React from "react";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";

import {
  Dialog,
  DialogContent,
  Button,
  Grid,
  Typography,
  Box,
} from "@mui/material";

import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel } from "react-responsive-carousel";

import Image from "../../util/Image";

import { ProjectsContext } from "../../contexts/Projects";
import { setCurrentProject } from "../../contexts/Projects/actions";
import { LanguageContext } from "../../contexts/Language";
import { t } from "../../contexts/Language/reducer";

// Styles object for better organization and reusability
const styles = {
  dialogContent: {
    padding: "24px",
  },
  title: {
    fontWeight: 600,
    marginBottom: "16px",
  },
  oneLiner: {
    marginBottom: "24px",
  },
  carouselContainer: {
    marginBottom: "24px",
  },
  actionButton: {
    width: "100%",
    padding: "12px",
    borderRadius: "8px",
    backgroundColor: "#f5f5f5",
    "&:hover": {
      backgroundColor: "#e0e0e0",
    },
  },
  buttonText: {
    color: "black",
    fontWeight: 500,
  },
};

interface ProjectOverviewDialogProps {
  project: any;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const ProjectOverviewDialog: React.FC<ProjectOverviewDialogProps> = ({ project, open, setOpen }) => {
  const { imageSources, oneLiner, title } = project;

  const [projectState, projectDispatch] = useContext(ProjectsContext);
  const [languageState] = useContext(LanguageContext);
  const navigate = useNavigate();

  const handleClose = () => setOpen(false);

  const handleOpenProject = () => {
    setCurrentProject(projectDispatch, project);
    navigate("/project");
  };

  // Filter images once outside of render
  const carouselImages = imageSources.filter(
    (image) => image.type === "LANDSCAPE" && image.inOverview
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogContent sx={styles.dialogContent}>
        <Typography variant="h5" align="center" sx={styles.title}>
          {t(title, languageState)}
        </Typography>

        <Box sx={styles.oneLiner}>
          <Typography variant="body1" align="center">
            {t(oneLiner, languageState)}
          </Typography>
        </Box>

        <Box sx={styles.carouselContainer}>
          {carouselImages.length > 0 && (
            <Carousel
              interval={3000}
              autoPlay={true}
              infiniteLoop={true}
              showStatus={false}
              showThumbs={carouselImages.length > 1}
            >
              {carouselImages.map((image, index) => (
                <Image
                  key={`${image.path}-${index}`}
                  filePath={image.path}
                  alt={`${t(title, languageState)} - ${index + 1}`}
                  className=""
                />
              ))}
            </Carousel>
          )}
        </Box>

        <Button
          variant="contained"
          onClick={handleOpenProject}
          sx={styles.actionButton}
        >
          <Typography sx={styles.buttonText}>
            {t("see-project", languageState)}
          </Typography>
        </Button>
      </DialogContent>
    </Dialog>
  );
};
